import { format } from 'date-fns'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'

import { formatDateOnly, formatTimeOnly } from '@/utils/date'

const TIME_INTERVAL_MINUTES = 30
const FIRST_TIME_SLOT_MINUTES = 8 * 60
const MINUTES_IN_DAY = 24 * 60

// Note: 30-minute slots from 8:00AM to the end of the day; value = minutes since midnight.
// Slots already in the past for the picked `date` (in the building's timezone) are disabled.
export const getScheduledTimeOptions = (date, timezone) => {
  const options = []

  for (
    let minutes = FIRST_TIME_SLOT_MINUTES;
    minutes < MINUTES_IN_DAY;
    minutes += TIME_INTERVAL_MINUTES
  ) {
    const hours24 = Math.floor(minutes / 60)
    const period = hours24 >= 12 ? 'PM' : 'AM'
    const hours12 = hours24 % 12 || 12
    const mins = String(minutes % 60).padStart(2, '0')

    options.push({
      label: `${hours12}:${mins}${period}`,
      value: minutes,
      isDisabled: isPastScheduledTime(date, minutes, timezone),
    })
  }

  return options
}

// Note: the picked day + time slot are wall-clock time in the building's timezone (the
// timezone the scheduled date is displayed in); falls back to the browser's timezone.
export const toScheduledTimestamp = (date, minutes, timezone) => {
  if (!date || minutes == null) return null

  const hours = String(Math.floor(minutes / 60)).padStart(2, '0')
  const mins = String(minutes % 60).padStart(2, '0')
  const wallClock = `${format(date, 'yyyy-MM-dd')}T${hours}:${mins}:00`

  return timezone ? fromZonedTime(wallClock, timezone).getTime() : new Date(wallClock).getTime()
}

export const fromScheduledTimestamp = (timestamp, timezone) => {
  if (!timestamp) return { date: null, time: null }

  const zoned = timezone ? toZonedTime(timestamp, timezone) : new Date(timestamp)

  return {
    date: new Date(zoned.getFullYear(), zoned.getMonth(), zoned.getDate()),
    time: zoned.getHours() * 60 + zoned.getMinutes(),
  }
}

// Note: "today" in the building's timezone — the earliest day a request can be scheduled for.
export const getScheduledMinDate = (timezone) => fromScheduledTimestamp(Date.now(), timezone).date

export const isPastScheduledTime = (date, minutes, timezone) => {
  const timestamp = toScheduledTimestamp(date, minutes, timezone)

  return timestamp != null && timestamp <= Date.now()
}

// Note: picking an earlier day can turn the already-picked slot into a past one — drop it
// so the user has to pick a valid time again.
export const withoutPastScheduledTime = ({ date, time }, timezone) => ({
  date,
  time: isPastScheduledTime(date, time, timezone) ? null : time,
})

// Note: editing an existing request - clearing either half clears both, so a scheduled date
// can be removed in one click instead of leaving a half-filled pair behind.
export const applyScheduledDateTimeChange = (current, changes, timezone) => {
  const isCleared = Object.values(changes).some((value) => value == null)
  if (isCleared) return { date: null, time: null }

  return withoutPastScheduledTime({ ...current, ...changes }, timezone)
}

// Note: a date without a time (or vice versa) can't be saved — matches mobile. When the
// building requires a scheduled date, both fields are mandatory.
export const getScheduledDateTimeValidity = (date, minutes, isRequired) => {
  const hasDate = !!date
  const hasTime = minutes != null

  return {
    isDateMissing: !hasDate && (isRequired || hasTime),
    isTimeMissing: !hasTime && (isRequired || hasDate),
  }
}

// Note: minutes since midnight as a wall-clock time (Jan 1 has no DST shift to skew it)
const formatMinutesOfDay = (minutes) => formatTimeOnly(new Date(2000, 0, 1, 0, minutes))

// Note: the resident's suggested timeframe - `date` is the day's midnight in the building's
// timezone, `start`/`end` are minutes since midnight. Locale-aware (en-US -> "07/15/2024
// 9:00 AM - 11:00 AM"); returns null when it's incomplete.
export const formatSuggestedTimeframe = ({ date, start, end }, timezone) => {
  if (!date || start == null || end == null) return null

  return `${formatDateOnly(date, timezone)} ${formatMinutesOfDay(start)} - ${formatMinutesOfDay(end)}`
}
